import { useState } from 'react';
import { Search, MapPin, Briefcase, DollarSign, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockJobs } from '../data/mock';
import { toast } from 'sonner';

const Jobs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);

  const filteredJobs = mockJobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = locationFilter === 'all' || job.location.includes(locationFilter);
    const matchesCategory = categoryFilter === 'all' || job.category === categoryFilter;
    return matchesSearch && matchesLocation && matchesCategory;
  });

  const handleApply = (jobTitle) => {
    toast.success(`Application submitted for ${jobTitle}! We'll be in touch soon.`);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-50 via-white to-brand-50/50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6 px-6 py-3 bg-brand-400/10 rounded-full border border-brand-400/20">
            <p className="text-brand-400 font-bold text-lg">💼 Current Opportunities</p>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
            Find Your Next <span className="text-brand-400">Opportunity</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-16 leading-relaxed">
            Explore exciting career opportunities in property, real estate, and strata management across Australia
          </p>

          {/* Search and Filters */}
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white border-gray-200 shadow-2xl">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="md:col-span-2">
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600" size={20} />
                      <Input
                        type="text"
                        placeholder="Search jobs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-12 bg-white border-gray-300 text-gray-900 py-6 text-lg"
                      />
                    </div>
                  </div>
                  <Select value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900 py-6">
                      <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      <SelectItem value="Sydney">Sydney</SelectItem>
                      <SelectItem value="Melbourne">Melbourne</SelectItem>
                      <SelectItem value="Brisbane">Brisbane</SelectItem>
                      <SelectItem value="Perth">Perth</SelectItem>
                      <SelectItem value="Adelaide">Adelaide</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900 py-6">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="Property Management">Property Management</SelectItem>
                      <SelectItem value="Strata Management">Strata Management</SelectItem>
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="Analysis">Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Jobs Listing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {filteredJobs.length} {filteredJobs.length === 1 ? 'Position' : 'Positions'} Available
            </h2>
          </div>

          {filteredJobs.length === 0 ? (
            <Card className="bg-white border-gray-200">
              <CardContent className="p-12 text-center">
                <p className="text-gray-600 text-lg">No jobs found matching your criteria. Try adjusting your filters.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Jobs List */}
              <div className="lg:col-span-1 space-y-4">
                {filteredJobs.map((job) => (
                  <Card 
                    key={job.id} 
                    className={`bg-white border-gray-200 hover:border-brand-400 transition-all duration-300 cursor-pointer ${
                      selectedJob?.id === job.id ? 'border-brand-400 bg-gray-100' : ''
                    }`}
                    onClick={() => setSelectedJob(job)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                        {job.featured && (
                          <span className="text-xs bg-brand-400/20 text-brand-400 px-2 py-1 rounded">
                            Featured
                          </span>
                        )}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <MapPin size={14} className="mr-2 text-brand-400" />
                          {job.location}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Briefcase size={14} className="mr-2 text-brand-400" />
                          {job.type}
                        </div>
                        <div className="flex items-center text-brand-400 font-semibold">
                          <DollarSign size={14} className="mr-2" />
                          {job.salary}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Job Details */}
              <div className="lg:col-span-2">
                {selectedJob ? (
                  <Card className="bg-white border-gray-200 sticky top-24">
                    <CardContent className="p-8">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedJob.title}</h2>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <MapPin size={16} className="mr-2 text-brand-400" />
                              {selectedJob.location}
                            </div>
                            <div className="flex items-center">
                              <Briefcase size={16} className="mr-2 text-brand-400" />
                              {selectedJob.type}
                            </div>
                            <div className="flex items-center">
                              <Clock size={16} className="mr-2 text-brand-400" />
                              Posted {new Date(selectedJob.postedDate).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        {selectedJob.featured && (
                          <span className="bg-brand-400/20 text-brand-400 px-3 py-1 rounded-full text-sm">
                            Featured
                          </span>
                        )}
                      </div>

                      <div className="mb-6">
                        <div className="text-2xl font-bold text-brand-400 mb-4">{selectedJob.salary}</div>
                        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded text-sm">
                          {selectedJob.category}
                        </span>
                      </div>

                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Job Description</h3>
                        <p className="text-gray-600 leading-relaxed">{selectedJob.description}</p>
                      </div>

                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Requirements</h3>
                        <ul className="space-y-3">
                          {selectedJob.requirements.map((req) => (
                            <li key={req} className="flex items-start text-gray-600">
                              <div className="w-2 h-2 bg-brand-400 rounded-full mt-2 mr-3"></div>
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <Button 
                        className="bg-brand-400 hover:bg-brand-500 text-white font-bold w-full text-lg py-7 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                        onClick={() => handleApply(selectedJob.title)}
                      >
                        Apply Now
                        <ArrowRight className="ml-2" size={20} />
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-white border-gray-200 h-full">
                    <CardContent className="p-12 flex items-center justify-center h-full">
                      <p className="text-gray-600 text-lg text-center">
                        Select a job to view details
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Jobs;
